import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Switch, Empty } from 'antd';
import { deriveModel } from './deriveTree';
import { useJourneyDetailQuery } from './data/queries';
import { insertTouchpointAfter, addBranch } from './edits';
import TimelineCanvas from './TimelineCanvas';
import DrawerPlaceholder from './DrawerPlaceholder';
import ReadOnlyNotice from './ReadOnlyNotice';
import './timeline.css';

const PILL = { LIVE: 'live', PAUSED: 'paused', MUTED: 'paused', DISABLED: 'off', ARCHIVED: 'off' };

export default function JourneyTimeline() {
  const { journeyId } = useParams();
  const history = useHistory();
  const { data, isLoading: loading, isError: error } = useJourneyDetailQuery(journeyId);
  // editing prototype: a local working copy of the raw model, seeded from the source data.
  // Mutations re-derive + re-layout reactively; nothing is persisted (resets on reload).
  const [work, setWork] = useState(null);
  useEffect(() => { setWork(data); }, [data]);
  const [showRelated, setShowRelated] = useState(true);
  const [showMetrics, setShowMetrics] = useState(true);
  const [drawer, setDrawer] = useState(null);

  const live = work || data;
  if (loading || !live) return <div className="tl-page" style={{ padding: 40 }}>Loading…</div>;
  if (error) return <div className="tl-page" style={{ padding: 40 }}><Empty description="Couldn't load journeys." /></div>;

  const onAddAfter = (jid, tpId) => { setWork((w) => insertTouchpointAfter(w || data, jid, tpId)); setDrawer({ title: 'Add touchpoint', subject: 'New touchpoint' }); };
  const onAddBranch = (tpId) => { setWork((w) => addBranch(w || data, tpId)); setDrawer({ title: 'Add branch', subject: 'New sub-journey' }); };

  const journey = live.journeys.find((j) => j.journey_id === Number(journeyId));
  if (!journey) {
    return (
      <div className="tl-page" style={{ padding: 40 }}>
        <Empty description="Journey not found." />
        <Button type="primary" onClick={() => history.push(history.location.pathname.startsWith('/preview') ? '/preview/timeline' : '/journeys')}>Back to journeys</Button>
      </div>
    );
  }

  const model = deriveModel(journey.journey_id, { journeys: live.journeys, segments: live.segments });

  return (
    <div className="tl-page">
      <div className="tl-header">
        <span className="tl-jname">{journey.name}<ReadOnlyNotice /></span>
        <span className={`tl-pill ${PILL[journey.state_enum] || 'off'}`}>● {journey.state_enum.charAt(0) + journey.state_enum.slice(1).toLowerCase()}</span>
        <span className="tl-spacer" />
        <span>Show Related Journeys</span>
        <Switch checked={showRelated} aria-label="toggle related journeys" onChange={setShowRelated} />
        <span>Show Metrics</span>
        <Switch checked={showMetrics} aria-label="toggle metrics" onChange={setShowMetrics} />
        <Button>Discard</Button>
        <Button type="primary">Save changes</Button>
      </div>
      <TimelineCanvas model={model} showRelated={showRelated} showMetrics={showMetrics}
        attributeById={live.attributeById}
        onEdit={(tp) => setDrawer({ title: 'Edit Touchpoint', subject: tp.name })}
        onSettings={(name) => setDrawer({ title: 'Journey Settings', subject: name })}
        onAddAfter={onAddAfter} onAddBranch={onAddBranch} />
      <DrawerPlaceholder open={!!drawer} title={drawer ? drawer.title : ''} subject={drawer ? drawer.subject : ''} onClose={() => setDrawer(null)} />
    </div>
  );
}
